import * as THREE from "three";

/**
 * Cart preview capture.
 *
 * The cart lists every configured item at the same size, so the render has to
 * be framed the same way every time. Grabbing the live viewport does not do
 * that - it bakes in whatever the customer happened to zoom and rotate to, and
 * usually leaves the model small in a lot of empty space.
 *
 * Instead we measure the model, put a throwaway camera at a fixed three-quarter
 * angle far enough back to just contain it, render one frame, and crop to the
 * aspect the cart column uses. The live camera is restored immediately, so the
 * customer never sees the swap.
 */

// Matches the cart's product image box (150x124 in cart-quotation.css).
const PREVIEW_ASPECT = 150 / 124;
const PREVIEW_WIDTH = 600;

// >1 leaves breathing room around the model so it never touches the edge.
const PREVIEW_PADDING = 1.14;

// Contact shadows are excluded from the measurement (see previewIgnore) because
// the plane they live on is far bigger than the product. They still render, so
// grow the measured box by this fraction of its footprint to keep the shadow
// that pools around the base inside the frame rather than clipped at the edge.
const GROUND_ALLOWANCE = 0.12;

// Fixed three-quarter view: slightly above, off to one side.
const PREVIEW_DIRECTION = new THREE.Vector3(1, 0.55, 1.15).normalize();

const PREVIEW_BACKGROUND = "#f7f7f7";
const JPEG_QUALITY = 0.86;

/**
 * World-space bounds of every visible mesh in the scene.
 *
 * Lights, the background colour and helpers contribute nothing, so this ends up
 * being the model alone. Returns null when there is nothing to measure.
 */
function measureScene(scene) {
  const box = new THREE.Box3();
  let found = false;

  scene.traverse((object) => {
    if (!object.isMesh || !object.visible || !object.geometry) return;

    // Ground/shadow planes are far wider than the product and would push the
    // camera right back. Tag them with userData.previewIgnore to leave them out
    // of the measurement - they still render, they just don't count.
    if (object.userData && object.userData.previewIgnore) return;

    // An invisible ancestor still renders nothing.
    let node = object.parent;
    while (node) {
      if (!node.visible) return;
      node = node.parent;
    }

    if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();
    if (!object.geometry.boundingBox) return;

    object.updateWorldMatrix(true, false);
    box.union(
      object.geometry.boundingBox.clone().applyMatrix4(object.matrixWorld),
    );
    found = true;
  });

  if (!found || box.isEmpty()) return null;

  // Room for the contact shadow, which spreads outward and downward from the
  // base. Vertical growth is downward only - nothing sits above the model.
  const size = box.getSize(new THREE.Vector3());
  const spread = Math.max(size.x, size.z) * GROUND_ALLOWANCE;
  box.min.x -= spread;
  box.max.x += spread;
  box.min.z -= spread;
  box.max.z += spread;
  box.min.y -= size.y * (GROUND_ALLOWANCE * 0.5);

  return box;
}

/**
 * The centred region of the drawing buffer matching PREVIEW_ASPECT.
 */
function cropRect(width, height) {
  const aspect = width / height;
  let cropWidth;
  let cropHeight;

  if (aspect > PREVIEW_ASPECT) {
    cropHeight = height;
    cropWidth = Math.round(height * PREVIEW_ASPECT);
  } else {
    cropWidth = width;
    cropHeight = Math.round(width / PREVIEW_ASPECT);
  }

  return {
    x: Math.round((width - cropWidth) / 2),
    y: Math.round((height - cropHeight) / 2),
    width: cropWidth,
    height: cropHeight,
  };
}

function toJpeg(canvas, crop) {
  const outputWidth = PREVIEW_WIDTH;
  const outputHeight = Math.round(PREVIEW_WIDTH / PREVIEW_ASPECT);

  const output = document.createElement("canvas");
  output.width = outputWidth;
  output.height = outputHeight;

  const context = output.getContext("2d");
  if (!context) return "";

  // The render is opaque, but fill anyway so a failed drawImage cannot leave
  // transparent pixels that JPEG would turn black.
  context.fillStyle = PREVIEW_BACKGROUND;
  context.fillRect(0, 0, outputWidth, outputHeight);
  context.drawImage(
    canvas,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  return output.toDataURL("image/jpeg", JPEG_QUALITY);
}

/**
 * @param {THREE.WebGLRenderer} renderer Needs preserveDrawingBuffer: true.
 * @param {THREE.Scene} scene
 * @param {THREE.Camera} camera The live camera, restored before returning.
 * @returns {string} JPEG data URI, or "" if the scene could not be captured.
 */
export function captureModelPreview(renderer, scene, camera) {
  if (!renderer || !scene || !camera) return "";

  const canvas = renderer.domElement;
  if (!canvas || !canvas.width || !canvas.height) return "";

  try {
    const crop = cropRect(canvas.width, canvas.height);
    const box = measureScene(scene);

    // Nothing measurable - fall back to the plain viewport grab so adding to
    // cart still produces an image.
    if (!box) return toJpeg(canvas, crop);

    const sphere = box.getBoundingSphere(new THREE.Sphere());
    if (!(sphere.radius > 0)) return toJpeg(canvas, crop);

    const canvasAspect = canvas.width / canvas.height;
    const previewCamera = new THREE.PerspectiveCamera(
      camera.isPerspectiveCamera ? camera.fov : 45,
      canvasAspect,
      0.01,
      Math.max(2000, sphere.radius * 100),
    );

    // Half-extents the crop covers at one unit of distance, as a fraction of
    // what the full frame covers. The tighter of the two sets the distance.
    const halfFov = THREE.MathUtils.degToRad(previewCamera.fov) / 2;
    const tan = Math.tan(halfFov);
    const verticalFit = crop.height / canvas.height;
    const horizontalFit = (crop.width / canvas.width) * canvasAspect;
    const fit = Math.min(verticalFit, horizontalFit);

    const distance = (sphere.radius * PREVIEW_PADDING) / (tan * fit);

    previewCamera.position
      .copy(sphere.center)
      .addScaledVector(PREVIEW_DIRECTION, distance);
    previewCamera.lookAt(sphere.center);
    previewCamera.updateProjectionMatrix();

    renderer.render(scene, previewCamera);
    const dataUrl = toJpeg(canvas, crop);

    // Put the customer's own view back before the next paint.
    renderer.render(scene, camera);

    return dataUrl;
  } catch {
    return "";
  }
}
